import React, { useState, useEffect, useRef } from 'react';
import useIsMobile from '../../Hooks/useIsMobile';
import { isLive } from '../../Utils/Utilities';
import { Button } from '../../Components/UI/button';

// 1. Define the Color Palette (3 Colors: Orange, Green, Sahi Dark Blue)
const themeColors = [
  { main: '#bf5700', bg: '#bf570015' }, // Sahi Orange
  { main: '#0b5d1e', bg: '#0b5d1e15' }, // Sahi Green
  { main: '#0d1f2d', bg: '#0c234015' }  // Sahi Dark Blue
];

const FeaturesScrollable = ({
  isAuthenticated = false,
}) => {
  const isMobile = useIsMobile();
  const [activeIndex, setActiveIndex] = useState(0);

  // Refs for the text sections to observe
  const sectionRefs = useRef([]);

  /*
    const features = [
    {
      id: 1,
      tag: 'Integration',
      title: 'Manage Inquiries <br> in a Single Platform',
      subtitle: 'Import and Manage Leads together',
      points: [
        'Integrate with IndiaMART, JustDial, TradeIndia, Meta forms, etc.',
        'Import existing inquiries from Excel',
        'Assign to Team & Contact quickly'
      ],
      image: isLive()
        ? '/Web/assets/Images/Landing/Features/1_import.png'
        : '/assets/Images/Landing/Features/1_import.png',
    },
    {
      id: 2,
      tag: 'Sales Pipeline',
      title: 'Maintain Stage-wise Sales Funnel',
      subtitle: 'Track Leads at each Stage till Conversion',
      points: [
        'Visualize Funnel through Kanban',
        'Control role-based Access for Team',
        'Monitor collective Record History'
      ],
      image: isLive()
        ? '/Web/assets/Images/Landing/Features/2_inquiry.png'
        : '/assets/Images/Landing/Features/2_inquiry.png',
    },
    {
      id: 3,
      tag: 'Performance',
      title: 'Monitor Team Efforts',
      subtitle: 'Observe & Compare Sales Performance',
      points: [
        'Track Efforts for Inquiry Conversion',
        'Empower Team to track against Targets',
        'Compare Performance through Leaderboard'
      ],
      image: isLive()
        ? '/Web/assets/Images/Landing/Features/3_team.png'
        : '/assets/Images/Landing/Features/3_team.png',
    },
    {
      id: 4,
      tag: 'Productivity',
      title: 'Manage Tasks <br> On-the-Go',
      subtitle: 'Track and Tick Sales Tasks',
      points: [
        'Simplify Daily Follow-ups',
        'Use Templates for Quick messages',
        'Get Timely App Alerts'
      ],
      image: isLive()
        ? '/Web/assets/Images/Landing/Features/4_tasks.png'
        : '/assets/Images/Landing/Features/4_tasks.png',
    },
    {
      id: 5,
      tag: 'Analytics',
      title: 'Analyze to Improve',
      subtitle: 'Access Powerful Dashboard and Reports',
      points: [
        "Know Customers' Sentiment",
        'Observe Product, Segment Popularity',
        'Learn from Rejection Reasons'
      ],
      image: isLive()
        ? '/Web/assets/Images/Landing/Features/5_report.png'
        : '/assets/Images/Landing/Features/5_report.png',
    }
  ];
  */

  const features = [
    // {
    //   id: 1,
    //   tag: 'Welcome',
    //   title: 'Welcome to <br> SahiClub!',
    //   subtitle: "Let's get your workspace ready",
    //   points: [
    //     'Discover the core features',
    //     'Manage your business opportunities',
    //     'Get started in few steps'
    //   ],
    //   image: isLive()
    //     ? '/Web/assets/Images/Illustrations/illustration.png'
    //     : '/assets/Images/Illustrations/illustration.png',
    // },
    {
      id: 2,
      // tag: 'Management',
      title: 'Smart <br> Management',
      subtitle: 'Streamline your operations',
      points: [
        'Finish Tasks Faster',
        'Delegate Reliably to Team',
        'Improve Meetings & Decisions'
      ],
      image: isLive()
        ? '/Web/assets/Images/Onboarding/Tasks.png'
        : '/assets/Images/Onboarding/Tasks.png',
    },
    {
      id: 3,
      // tag: 'Sales Growth',
      title: 'Smart <br> Sales Growth',
      subtitle: 'Boost conversion and repeat orders',
      points: [
        'Track Lead Pipeline',
        'Monitor Team Performance',
        'Increase Repeat Orders'
      ],
      image: isLive()
        ? '/Web/assets/Images/Onboarding/Inquiries.png'
        : '/assets/Images/Onboarding/Inquiries.png',
    },
    {
      id: 4,
      // tag: 'Networking',
      title: 'Smart <br> Networking',
      subtitle: 'Connect with B2B Community (coming soon)',
      points: [
        'Promote on B2B Community',
        'Get Business Opportunities',
        'Find Better Suppliers'
      ],
      image: isLive()
        ? '/Web/assets/Images/Onboarding/Network.png'
        : '/assets/Images/Onboarding/Network.png',
    }
  ];


  const scrollToTop = () => {
    !isAuthenticated ? scrollToSection('mainLanding') : scrollToSection('pkgAndPricing');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 3. Scroll Logic: Intersection Observer
  useEffect(() => {
    if (isMobile) return;

    const observerOptions = {
      root: null,
      // This margin creates a narrow "active line" in the exact center of the screen
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'));
          setActiveIndex(index);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [isMobile]);

  const getColor = (idx) => themeColors[idx % themeColors.length];

  if (isMobile) return null;

  return (
    // 'relative' is crucial here. It defines the boundary for the sticky element.
    <section className="relative w-full bg-white">
      <div className="max-w-[1300px] mx-auto flex px-6 lg:px-12">

        {/* --- LEFT SIDE: Scrollable Content --- */}
        {/* We removed overflow-y-auto. This div will grow naturally with content. */}
        <div className="w-[40%] flex flex-col">
          {features.map((feature, idx) => {
            const colorTheme = getColor(idx);
            const isActive = idx === activeIndex;

            return (
              <div
                key={idx}
                ref={(el) => (sectionRefs.current[idx] = el)}
                data-index={idx}
                // 'min-h-screen' ensures each feature takes a full scroll-length to pass
                className={`min-h-screen flex flex-col justify-center py-20 transition-all duration-700 ease-out ${
                  // Scale and opacity effect for the text
                  isActive ? 'opacity-100 translate-x-0' : 'opacity-30 blur-[2px] -translate-x-4'
                  }`}
              >
                {/* Feature Tag */}
                {/* <span
                  className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-6 w-fit transition-colors duration-500"
                  style={{
                    backgroundColor: colorTheme.bg,
                    color: colorTheme.main
                  }}
                >
                  {feature.tag}
                </span> */}

                {/* Main Title */}
                <h2
                  className="text-4xl lg:text-5xl font-bold text-slate-800 mb-4"
                  style={{ lineHeight: '1.25' }}
                  dangerouslySetInnerHTML={{
                    __html: feature.title.replace(
                      /(Smart)/g,
                      `<span style="color: ${colorTheme.main}">$1</span>`
                    )
                  }}
                >
                </h2>

                {feature.subtitle && (
                  <p className="text-lg text-slate-700 mb-8 font-medium">
                    {feature.subtitle}
                  </p>
                )}

                {/* Bullet Points */}
                <div className="space-y-4 mb-10">
                  {feature.points.map((point, pIdx) => (
                    <div key={pIdx} className="flex items-center gap-3">
                      <div
                        className="rounded-full p-1 transition-colors duration-500"
                        style={{ backgroundColor: colorTheme.bg }}
                      >
                        <svg
                          className="w-5 h-5 transition-colors duration-500"
                          style={{ color: colorTheme.main }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span
                        className="text-lg text-slate-700"
                        dangerouslySetInnerHTML={{
                          __html: point.replace(
                            /<strong>(.*?)<\/strong>/g,
                            `<strong style="color: ${colorTheme.main}; font-weight: 600;">$1</strong>`
                          )
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Button
                  className="px-7 py-2 font-semibold text-md w-fit text-white border transition-all duration-300"
                  style={{
                    backgroundColor: colorTheme.main,
                    borderColor: colorTheme.main,
                    color: 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = colorTheme.main;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colorTheme.main;
                    e.currentTarget.style.color = 'white';
                  }}
                  onClick={scrollToTop}
                >
                  Get Started
                </Button>
              </div>
            );
          })}
        </div>

        {/* --- RIGHT SIDE: Sticky Visuals --- */}
        {/* sticky top-0 h-screen makes this element lock to the viewport while the parent section is visible */}
        <div className="w-[60%] sticky top-0 h-screen flex items-center justify-center">
          <div className="relative w-full max-w-[550px] aspect-square flex items-center justify-center">

            {/* 1. Background SVG Shape */}
            <div
              className="absolute inset-0 z-0 right-0 transform scale-[263%] transition-all duration-700 ease-in-out opacity-10
			pointer-events-none"
              style={{
                backgroundColor: getColor(activeIndex).main,
                WebkitMaskImage: `url(${isLive()
                  ? "/Web/assets/Images/SahiCRM_svg.svg"
                  : "/assets/Images/SahiCRM_svg.svg"
                  })`,
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                WebkitMaskSize: "contain",
                maskImage: `url(${isLive()
                  ? "/Web/assets/Images/SahiCRM_svg.svg"
                  : "/assets/Images/SahiCRM_svg.svg"
                  })`,
                maskRepeat: "no-repeat",
                maskPosition: "center",
                maskSize: "contain",
              }}
            />



            {/* 2. Image Container */}
            {/* Added 'h-full' here so the container fills the aspect-square parent */}
            <div className="relative z-10 w-full h-full">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className={`absolute top-1/2 left-1/2 w-full transition-all duration-700 ease-in-out transform ${activeIndex === idx
                    ? 'opacity-100 -translate-x-1/2 -translate-y-1/2 scale-100 blur-0'
                    : 'opacity-0 -translate-x-1/2 -translate-y-[30%] scale-90 blur-sm pointer-events-none'
                    }`}
                >
                  <div className="max-w-[85%] mx-auto overflow-hidden select-none">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-auto object-contain rounded-xl"
                    />
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default FeaturesScrollable;